import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  const [step, setStep] = useState(1); // 1: email/details, 2: otp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn, signUp } = useAuth();
  const [countries, setCountries] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    countryCode: '',
    phone: '',
    otp: '',
  });

  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await apiRequest(`${BACKEND_URL}/api/admin/public/countries`, {
        method: 'GET',
      });

      if (response.ok && response.data.countries.length > 0) {
        setCountries(response.data.countries);
        // Set first country as default
        const firstCountry = response.data.countries[0];
        setFormData(prev => ({
          ...prev,
          country: firstCountry.name,
          countryCode: firstCountry.phone_code || firstCountry.code || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Fallback to default
      setFormData(prev => ({
        ...prev,
        country: 'India',
        countryCode: '+91',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If country changes, update country code (use international dial code)
    if (name === 'country') {
      const selectedCountry = countries.find(c => c.name === value);
      setFormData({
        ...formData,
        country: value,
        countryCode: selectedCountry ? (selectedCountry.phone_code || selectedCountry.code || '') : '',
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    setError('');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'signin' ? '/api/auth/signin/send-otp' : '/api/auth/signup/send-otp';
      const payload = mode === 'signin' 
        ? { email: formData.email }
        : { 
            name: formData.name, 
            email: formData.email, 
            phone: `${formData.countryCode}${formData.phone}`,
            country: formData.country
          };

      const response = await apiRequest(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = response.data;
        // If user doesn't exist during signin, switch to signup
        if (mode === 'signin' && response.data?.detail?.includes('User not found')) {
          setMode('signup');
          setError('Account not found. Please sign up below.');
          setLoading(false);
          return;
        }
        // If user already exists during signup, switch to signin
        if (mode === 'signup' && response.data?.detail?.includes('already exists')) {
          setMode('signin');
          setError('Account already exists. Please sign in below.');
          setLoading(false);
          return;
        }
        setError(response.data?.detail || 'Failed to send OTP. Please try again.');
        setLoading(false);
        return;
      }

      // Success - move to OTP verification step
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        email: formData.email,
        otp: formData.otp,
        ...(mode === 'signup' && { 
          name: formData.name, 
          phone: `${formData.countryCode}${formData.phone}`,
          country: formData.country
        })
      };

      const response = await apiRequest(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Handle specific error cases
        setError(response.data?.detail || 'Invalid or expired OTP. Please try again.');
        setLoading(false);
        return;
      }

      // Success - Save token and user data
      const data = response.data;
      localStorage.setItem('auth_token', data.token);
      
      if (mode === 'signin') {
        signIn(data.user);
      } else {
        signUp(data.user);
      }
      
      setLoading(false);
      onSuccess(mode);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const resetForm = (newMode = 'signin') => {
    const firstCountry = countries.length > 0 ? countries[0] : { name: 'India', phone_code: '+91' };
    setStep(1);
    setMode(newMode);
    setError('');
    setFormData({ 
      name: '', 
      email: '', 
      country: firstCountry.name,
      countryCode: firstCountry.phone_code || firstCountry.code || '+91',
      phone: '', 
      otp: '' 
    });
  };

  const switchMode = () => {
    const newMode = mode === 'signin' ? 'signup' : 'signin';
    resetForm(newMode);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[2rem] max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-3xl font-bold text-navy mb-2">
          {mode === 'signin' ? 'Sign In' : 'Sign Up'}
        </h2>
        <p className="text-gray-600 mb-6">
          {mode === 'signin'
            ? 'Enter your email to continue'
            : 'Create your account to get started'}
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleSendOTP} className="space-y-6">
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="John Doe"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-navy mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                placeholder="john@example.com"
                required
              />
            </div>

            {mode === 'signup' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Country
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  >
                    {countries.length === 0 && (
                      <option value="">Loading countries...</option>
                    )}
                    {countries.map((country) => (
                      <option key={country.id} value={country.name}>
                        {country.flag} {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Phone Number
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.countryCode}
                      readOnly
                      className="w-20 px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 text-center font-semibold"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      placeholder="1234567890"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-6 text-base font-semibold disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">
                Enter OTP
              </label>
              <p className="text-sm text-gray-600 mb-3">
                We've sent a verification code to {formData.email}
              </p>
              <input
                type="text"
                name="otp"
                value={formData.otp}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength="6"
                required
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-6 text-base font-semibold disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </div>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-sm text-gray-600 hover:text-navy"
            >
              Change email
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={switchMode}
            className="text-sm text-gray-600 hover:text-navy"
          >
            {mode === 'signin'
              ? "Don't have an account? Sign Up"
              : 'Already have an account? Sign In'}
          </button>
        </div>
      </div>
    </div>
  );
};
